import { ErrorApi, claseSegunEstado } from './errores'

/**
 * Cliente HTTP del portal.
 *
 * **El portal nunca llama a `/v1/`. Llama a `/interna/`.** Que la fachada
 * versionada exista no es asunto de la pantalla.
 *
 * Este archivo es el transporte: cabeceras, errores y la regla de reintento.
 * Las funciones por recurso se generan desde OpenAPI en `api/generado/` y no se
 * escriben a mano.
 */

export const BASE_INTERNA = '/interna'

export interface OpcionesPeticion {
  /** Parámetros de consulta. Los `undefined` no se envían. */
  consulta?: Record<string, string | number | boolean | undefined | null>
  /**
   * Cuerpo JSON, o un `FormData` para `multipart/form-data`. Con `FormData` no
   * se fija `Content-Type`: el navegador lo pone con el `boundary`, y fijarlo a
   * mano lo pierde y el servidor no puede partir el cuerpo.
   */
  cuerpo?: unknown
  /**
   * Clave de idempotencia. **Sin ella una escritura no se reintenta jamás**,
   * ni automáticamente ni a petición del usuario.
   * La generan los hooks de dominio; ninguna pantalla la construye a mano.
   */
  claveIdempotencia?: string
  senal?: AbortSignal
  /** Reintentos ante red caída o 5xx. Solo se aplican si hay clave. */
  reintentos?: number
}

type Metodo = 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE'

function construirUrl(base: string, ruta: string, consulta: OpcionesPeticion['consulta']): string {
  const url = ruta.startsWith(base) ? ruta : `${base}${ruta.startsWith('/') ? '' : '/'}${ruta}`
  if (!consulta) return url

  const parametros = new URLSearchParams()
  for (const [clave, valor] of Object.entries(consulta)) {
    if (valor === undefined || valor === null || valor === '') continue
    parametros.set(clave, String(valor))
  }
  const texto = parametros.toString()
  return texto === '' ? url : `${url}?${texto}`
}

async function leerError(respuesta: Response): Promise<ErrorApi> {
  const crudo = await respuesta.text().catch(() => '')
  let mensaje = respuesta.statusText || 'Error de la API'
  let codigo: string | undefined

  try {
    // La API habla **ProblemDetails** (RFC 9457): `type`, `title`, `status`,
    // `detail`. Aquí se leían `mensaje`/`message`, que no existen en ninguna
    // respuesta del servidor, así que el `??` caía siempre al final y **se
    // perdía el mensaje de todos los errores**: el usuario veía el
    // `statusText` de HTTP —«Bad Request»— en vez de lo que había pasado.
    //
    // `detail` va antes que `title` porque es el específico: «El e-NCF
    // E310000000003 ya fue emitido» frente a «La solicitud no es válida».
    //
    // Se conservan `mensaje`/`message` al final por si algún intermediario
    // —una pasarela, un proxy— devuelve un error con su propia forma.
    const cuerpo = JSON.parse(crudo) as {
      detail?: string
      title?: string
      type?: string
      mensaje?: string
      message?: string
      codigo?: string
      code?: string
    }

    mensaje = cuerpo.detail ?? cuerpo.title ?? cuerpo.mensaje ?? cuerpo.message ?? mensaje

    // `type` es el discriminante estable: una URI que no cambia al reescribir
    // el texto ni al traducirlo. Decidir por el mensaje en español es cómo se
    // rompe un cliente cuando alguien corrige una tilde.
    codigo = cuerpo.type ?? cuerpo.codigo ?? cuerpo.code
  } catch {
    // El cuerpo no era JSON. Se conserva crudo para soporte.
  }

  return new ErrorApi({
    mensaje,
    clase: claseSegunEstado(respuesta.status),
    estado: respuesta.status,
    codigo,
    crudo: crudo === '' ? undefined : crudo,
  })
}

/**
 * De dónde sale cada cosa que el transporte no debe decidir por su cuenta.
 *
 * Existe porque hay **dos clientes con dos testigos**: el portal (`/interna`) y
 * la consola del dueño (`/consola`, `docs/09-consola-del-dueno.md`). Si
 * compartieran el lector de testigo, entrar a la consola dejaría al portal
 * mandando el testigo del operador —o al revés—, y sacar a uno por un 401
 * sacaría también al otro.
 */
export interface ConfiguracionCliente {
  /** Prefijo que se antepone a las rutas relativas: `/interna`, `/consola`. */
  base: string
  leerTestigo: () => string | null | undefined
  alCaducarSesion: () => void
}

async function peticion<T>(
  configuracion: ConfiguracionCliente,
  metodo: Metodo,
  ruta: string,
  opciones: OpcionesPeticion = {},
): Promise<T> {
  const { consulta, cuerpo, claveIdempotencia, senal, reintentos } = opciones
  const esFormulario = typeof FormData !== 'undefined' && cuerpo instanceof FormData

  const esEscritura = metodo !== 'GET'
  // La capa de datos solo reintenta escrituras que llevan clave de
  // idempotencia. Un POST sin clave no se reintenta jamás.
  const intentosMaximos = esEscritura
    ? claveIdempotencia === undefined
      ? 1
      : (reintentos ?? 2) + 1
    : (reintentos ?? 2) + 1

  const cabeceras: Record<string, string> = { Accept: 'application/json' }
  if (cuerpo !== undefined && !esFormulario) cabeceras['Content-Type'] = 'application/json'
  if (claveIdempotencia !== undefined) cabeceras['Idempotency-Key'] = claveIdempotencia

  // El testigo se lee **en cada intento**, no una vez al construir el cliente.
  // Si se capturara al arrancar, un reintento tras una renovación seguiría
  // mandando el testigo viejo y fallaría con 401 justo después de haberse
  // renovado — el peor momento, porque el usuario acaba de volver.
  const testigo = configuracion.leerTestigo()
  if (testigo !== undefined && testigo !== null) cabeceras.Authorization = `Bearer ${testigo}`

  let ultimoError: ErrorApi | null = null

  for (let intento = 1; intento <= intentosMaximos; intento += 1) {
    try {
      const respuesta = await fetch(construirUrl(configuracion.base, ruta, consulta), {
        method: metodo,
        headers: cabeceras,
        body:
          cuerpo === undefined
            ? undefined
            : esFormulario
              ? (cuerpo as FormData)
              : JSON.stringify(cuerpo),
        credentials: 'same-origin',
        signal: senal,
      })

      if (respuesta.ok) {
        if (respuesta.status === 204) return undefined as T
        const texto = await respuesta.text()
        return (texto === '' ? undefined : JSON.parse(texto)) as T
      }

      const error = await leerError(respuesta)

      // El 401 sigue siendo necesario como red de seguridad aunque la
      // renovación del token sea proactiva (docs/00, §4.6).
      if (error.clase === 'autenticacion') {
        configuracion.alCaducarSesion()
        throw error
      }

      if (!error.esReintentable || intento === intentosMaximos) throw error
      ultimoError = error
    } catch (fallo) {
      if (fallo instanceof ErrorApi) {
        if (!fallo.esReintentable || intento === intentosMaximos) throw fallo
        ultimoError = fallo
      } else if (fallo instanceof DOMException && fallo.name === 'AbortError') {
        throw fallo
      } else {
        // El navegador no llegó al servidor: no sabemos nada de DGII.
        const error = new ErrorApi({
          mensaje: 'Sin conexión con el servidor',
          clase: 'red',
          estado: 0,
          crudo: fallo instanceof Error ? fallo.message : undefined,
        })
        if (intento === intentosMaximos) throw error
        ultimoError = error
      }
    }

    await esperar(200 * 2 ** (intento - 1))
  }

  throw ultimoError ?? new ErrorApi({ mensaje: 'Error desconocido', clase: 'desconocido', estado: 0 })
}

function esperar(ms: number): Promise<void> {
  return new Promise((resolver) => setTimeout(resolver, ms))
}

/** Lo instala la capa de sesión para sacar al usuario al caducar el token. */
let alCaducarSesion: (() => void) | undefined
export function instalarManejadorDeSesionCaducada(manejador: () => void): void {
  alCaducarSesion = manejador
}

/**
 * De dónde sale el testigo que va en `Authorization`.
 *
 * Es una función y no un valor a propósito: el transporte no debe saber
 * **dónde** vive el testigo —memoria, almacenamiento, un refresco en curso—
 * solo pedirlo cuando le hace falta. La capa de sesión decide lo demás.
 */
let leerTestigo: (() => string | null) | undefined
export function instalarLectorDeTestigo(lector: () => string | null): void {
  leerTestigo = lector
}

export interface ClienteHttp {
  obtener: <T>(ruta: string, opciones?: OpcionesPeticion) => Promise<T>
  crear: <T>(ruta: string, opciones?: OpcionesPeticion) => Promise<T>
  reemplazar: <T>(ruta: string, opciones?: OpcionesPeticion) => Promise<T>
  modificar: <T>(ruta: string, opciones?: OpcionesPeticion) => Promise<T>
  eliminar: <T>(ruta: string, opciones?: OpcionesPeticion) => Promise<T>
}

/**
 * Un cliente con su propio prefijo y su propio testigo. Mismo transporte,
 * mismas reglas de reintento y de error: lo único que cambia es de quién es la
 * sesión.
 */
export function crearClienteHttp(configuracion: ConfiguracionCliente): ClienteHttp {
  return {
    obtener: <T>(ruta: string, opciones?: OpcionesPeticion) => peticion<T>(configuracion, 'GET', ruta, opciones),
    crear: <T>(ruta: string, opciones?: OpcionesPeticion) => peticion<T>(configuracion, 'POST', ruta, opciones),
    reemplazar: <T>(ruta: string, opciones?: OpcionesPeticion) => peticion<T>(configuracion, 'PUT', ruta, opciones),
    modificar: <T>(ruta: string, opciones?: OpcionesPeticion) => peticion<T>(configuracion, 'PATCH', ruta, opciones),
    eliminar: <T>(ruta: string, opciones?: OpcionesPeticion) => peticion<T>(configuracion, 'DELETE', ruta, opciones),
  }
}

/**
 * Descarga un archivo del portal (el TXT de la TSS, el CSV de pago) con el
 * testigo de la sesión, y se lo entrega al navegador con el nombre que pone el
 * servidor.
 *
 * Va aparte de `peticion` porque la respuesta no es JSON: el archivo se baja tal
 * cual, byte a byte. El TXT de la TSS es ISO-8859-1 y de ancho fijo, y pasarlo
 * por texto lo reescribiría en UTF-8 y correría sus posiciones.
 */
export async function descargarArchivo(ruta: string, consulta?: OpcionesPeticion['consulta']): Promise<void> {
  const cabeceras: Record<string, string> = {}
  const testigo = leerTestigo?.()
  if (testigo !== undefined && testigo !== null) cabeceras.Authorization = `Bearer ${testigo}`

  const respuesta = await fetch(construirUrl(BASE_INTERNA, ruta, consulta), {
    headers: cabeceras,
    credentials: 'same-origin',
  })

  if (!respuesta.ok) {
    const error = await leerError(respuesta)
    if (error.clase === 'autenticacion') alCaducarSesion?.()
    throw error
  }

  const disposicion = respuesta.headers.get('Content-Disposition') ?? ''
  const nombre =
    /filename\*=UTF-8''([^;]+)/i.exec(disposicion)?.[1] ?? /filename="?([^";]+)"?/i.exec(disposicion)?.[1] ?? 'archivo'

  const url = URL.createObjectURL(await respuesta.blob())
  const enlace = document.createElement('a')
  enlace.href = url
  enlace.download = decodeURIComponent(nombre)
  enlace.click()
  URL.revokeObjectURL(url)
}

/** El cliente del portal. Sus ganchos los instala `dominio/sesion/sesion.ts`. */
export const api: ClienteHttp = crearClienteHttp({
  base: BASE_INTERNA,
  leerTestigo: () => leerTestigo?.(),
  alCaducarSesion: () => alCaducarSesion?.(),
})
